/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.1:gemini-1.5-flash (PREV-3) - Modified `handleSendMessage` to include the last 20 messages (user and assistant)
 *                                   as `conversation_history` in the payload sent to the `/chat` endpoint.
 * 1.0.0:gemini-1.5-flash (PREV-4) - Initial creation of the AIAssistantPage component.
 *                                   - Implemented conversation display and message input.
 *                                   - Integrated apiClient for posting user messages to /chat.
 *                                   - Added auto-scrolling to the latest message.
 * 1.1.0:gemini-1.5-flash (PREV-2) - Implemented long-polling for the `/chat` endpoint.
 *                                   - When sending a message, it now expects a `poll` ID or a final `message`.
 *                                   - Uses `setTimeout` to poll `/chat?pid=long-poll-id` every 3 seconds until a final response is received.
 * 1.2.0:gemini-1.5-flash (PREV-1) - Implemented conversation persistence using IndexedDB.
 *                                   - Loads conversation on component mount and saves on message updates.
 *                                   - Added a "New Conversation" button to clear the stored conversation.
 * 1.3.0:gemini-1.5-flash (PREV) - Added functionality to attach files to messages.
 *                                   - Users can now select multiple files (images, PDFs, text, Word docs).
 *                                   - Files are converted to base64 and sent in the `files` array within the message payload.
 *                                   - Includes an "Attach File" link that works for mobile picture uploads.
 *                                   - Implemented basic client-side file size validation.
 * 1.4.0:gemini-1.5-flash (CURRENT) - Restricted file uploads to audio, image, and text file types only.
 *
 * -----
 * SECURITY_RISK: MEDIUM - This component interacts with the /chat API endpoint, sending user input, conversation history, and now file attachments.
 *                         The long-polling mechanism introduces repeated requests.
 *                         It persists conversation history locally in IndexedDB.
 *                         File attachments are base64 encoded and sent in the JSON payload, which could be large.
 *                         While the UI itself doesn't handle sensitive data directly, the backend
 *                         interaction could involve processing user queries and file content which might contain sensitive information.
 * INTENT: Provide a conversational interface for users to interact with an AI assistant,
 *         enabling natural language queries and responses, with added context from recent conversation history,
 *         supporting a long-polling mechanism for potentially delayed AI responses, persisting conversations
 *         across sessions for a continuous user experience, and allowing users to provide additional context
 *         through file attachments.
 * PROMPTER_INSTRUCTIONS: Ensure message posting is robust and error handling is clear.
 *                        Consider sanitizing user input and file content before sending to the backend.
 *                        Verify the long-polling mechanism correctly handles `poll` IDs and final responses.
 *                        Ensure IndexedDB operations for persistence are robust and handle errors gracefully.
 *                        Monitor payload size for file attachments and consider alternative upload methods for very large files.
 * LLM_CONFIDENCE: 95% - The core functionality is implemented, but advanced features like
 *                        real-time updates, comprehensive error display for individual messages,
 *                        and input sanitization might require further refinement. The addition of conversation history,
 *                        long-polling, IndexedDB persistence, and file attachments are implemented according to specifications.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '../components/ui/input.tsx';
import { Button } from '../components/ui/button.tsx';
import { apiClient, ApiError } from '../api/apiClient.tsx';
import { Spinner } from '../components/ui/spinner.tsx';
import { toast } from 'react-toastify';
import { displayGlobalError } from '../utils/globalErrorHandler.ts';
import { ChatMessage, ChatApiResponse, AttachedFile } from '../types/chat.d';
import ReactMarkdown from "react-markdown";
import { getConversation, saveConversation, clearConversation } from '../utils/chatIndexedDB.ts';
import { X } from 'lucide-react';

const MAX_FILE_SIZE_MB = 5; // Maximum file size in MB
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const AIAssistantPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // NEW: Load conversation from IndexedDB on component mount
  useEffect(() => {
    const loadStoredConversation = async () => {
      try {
        const storedMessages = await getConversation();
        if (storedMessages.length > 0) {
          setMessages(storedMessages);
        }
      } catch (error) {
        console.error("Failed to load conversation from IndexedDB:", error);
        displayGlobalError("Failed to load previous conversation.", 0);
      }
    };
    loadStoredConversation();
  }, []); // Run only once on mount

  // NEW: Save conversation to IndexedDB whenever messages state changes
  useEffect(() => {
    if (messages.length > 0) {
      saveConversation(messages).catch(error => {
        console.error("Failed to save conversation to IndexedDB:", error);
        displayGlobalError("Failed to save conversation history.", 0);
      });
    } else {
      // If messages become empty, clear IndexedDB as well
      clearConversation().catch(error => {
        console.error("Failed to clear conversation from IndexedDB:", error);
        displayGlobalError("Failed to clear conversation history.", 0);
      });
    }
  }, [messages]); // Run whenever messages change

  // Scroll to the bottom of the conversation whenever messages change
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cleanup for polling timeout
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const handlePollingError = useCallback((error: unknown, pollId: string | null) => {
    const errorMessage = error instanceof ApiError ? error.message : 'An unexpected error occurred during polling.';
    displayGlobalError(`Polling failed for ID ${pollId}: ${errorMessage}`, (error as ApiError)?.status || 0);
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: Date.now().toString() + '-error',
        text: `Error polling for response (ID: ${pollId}): ${errorMessage}`,
        sender: 'assistant',
        timestamp: new Date().toISOString(),
      },
    ]);
    setIsSending(false); // Stop sending/polling state
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, [displayGlobalError]);

  // Function to start or continue polling
  const startPolling = useCallback(async (pollId: string) => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }

    pollingTimeoutRef.current = setTimeout(async () => {
      try {
        const pollResponse = await apiClient<ChatApiResponse>(`/chat?pid=${pollId}`, { method: 'GET' });

        if (pollResponse.poll) {
          // Continue polling with the new poll ID
          startPolling(pollResponse.poll);
        } else if (pollResponse.message) {
          // Final response received
          toast.dismiss();
          const assistantResponse: ChatMessage = {
            id: Date.now().toString() + '-ai',
            text: pollResponse.message,
            sender: 'assistant',
            timestamp: new Date().toISOString(),
          };
          setMessages((prevMessages) => [...prevMessages, assistantResponse]);
          setIsSending(false); // Stop sending/polling state
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
        } else {
          // Unexpected response format
          handlePollingError(new Error('Unexpected response format from polling endpoint.'), pollId);
        }
      } catch (error: unknown) {
        handlePollingError(error, pollId);
      }
    }, 3000); // Poll every 3 seconds
  }, [setMessages, setIsSending, handlePollingError]);

  // NEW: Handle file selection
  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const newAttachedFiles: AttachedFile[] = [];

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File "${file.name}" is too large (max ${MAX_FILE_SIZE_MB}MB).`);
        continue;
      }

      try {
        const base64Content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = error => reject(error);
          reader.readAsDataURL(file);
        });

        newAttachedFiles.push({
          name: file.name,
          type: file.type,
          content: base64Content,
        });
      } catch (error) {
        console.error("Error reading file:", file.name, error);
        toast.error(`Failed to read file "${file.name}".`);
      }
    }

    setAttachedFiles((prev) => [...prev, ...newAttachedFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Clear the input so the same file can be selected again
    }
  }, []);

  // NEW: Handle removing an attached file
  const handleRemoveFile = useCallback((fileName: string) => {
    setAttachedFiles((prev) => prev.filter((file) => file.name !== fileName));
  }, []);


  const handleSendMessage = useCallback(async () => {
    if (inputMessage.trim() === '' && attachedFiles.length === 0) return;

    const newUserMessage: ChatMessage = {
      id: Date.now().toString(), // Simple unique ID
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString(),
      files: attachedFiles.length > 0 ? attachedFiles : undefined,
    };

    setMessages((prevMessages) => [...prevMessages, newUserMessage]);
    setInputMessage('');
    setAttachedFiles([]); // NEW: Clear attached files after sending
    setIsSending(true);

    try {
      // Get the last 20 messages (user and assistant) for context
      const conversationHistory = messages.slice(Math.max(0, messages.length - 60));

      let nofilesConversationHistory: ChatMessage[] = [];
      conversationHistory.map((message) => {
        let nofiles_message: ChatMessage = {
          id: '',
          text: '',
          timestamp: '',
          files: [],
          sender: 'user'
        };
        Object.keys(message).filter((item) => item != 'files').map((key) => nofiles_message[key] = message[key]);
        nofilesConversationHistory.push(nofiles_message);
      });

      // Post the new user message to the /chat endpoint, including conversation history and files
      const response = await apiClient<ChatApiResponse>('/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: newUserMessage.text, // Send only the new message text
          conversation_history: nofilesConversationHistory, // Include conversation history
          files: newUserMessage.files, // NEW: Include files in the payload
        }),
      });

      if (response.poll) {
        // Start long-polling
        startPolling(response.poll);
      } else if (response.message) {
        // Direct response, no polling needed
        const assistantResponse: ChatMessage = {
          id: Date.now().toString() + '-ai',
          text: response.message,
          sender: 'assistant',
          timestamp: new Date().toISOString(),
        };
        setMessages((prevMessages) => [...prevMessages, assistantResponse]);
        setIsSending(false);
      } else {
        // Unexpected response format from initial POST
        displayGlobalError('Unexpected response format from chat endpoint.', 0);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: Date.now().toString() + '-error',
            text: 'Error: Unexpected response from AI assistant.',
            sender: 'assistant',
            timestamp: new Date().toISOString(),
          },
        ]);
        setIsSending(false);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof ApiError ? error.message : 'An unexpected error occurred.';
      displayGlobalError(`Failed to send message: ${errorMessage}`, (error as ApiError)?.status || 0);
      // Optionally, add an error message to the chat itself
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: Date.now().toString() + '-error',
          text: `Error: ${errorMessage}`,
          sender: 'assistant', // Or a special 'error' sender
          timestamp: new Date().toISOString(),
        },
      ]);
      setIsSending(false);
    } finally {
      document.getElementsByClassName('user-message-box')[0].focus();
    }
  }, [inputMessage, messages, attachedFiles, displayGlobalError, startPolling]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isSending) {
      handleSendMessage();
    }
  }, [handleSendMessage, isSending]);

  // NEW: Handler for starting a new conversation
  const handleNewConversation = useCallback(async () => {
    if (window.confirm("Are you sure you want to start a new conversation? This will clear all current messages.")) {
      setMessages([]); // Clear local state
      setAttachedFiles([]); // NEW: Clear attached files
      await clearConversation(); // Clear from IndexedDB
      toast.info("New conversation started!");
    }
  }, []);

  return (
    <div className="flex flex-col min-h-[600px] h-[calc(100vh-320px)] bg-background rounded-lg shadow-md p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">AI Assistant</h2>
        {/* NEW: New Conversation Button */}
        <Button
          onClick={handleNewConversation}
          variant="outline"
          disabled={isSending}
        >
          New Conversation
        </Button>
      </div>
      <div className="flex-grow overflow-y-auto space-y-4 p-2 border rounded-md bg-muted/20">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            Start a conversation with your AI Assistant!
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] p-3 rounded-lg shadow-sm ${
                  msg.sender === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground'
                }`}
              >
                <p className="text-sm"><ReactMarkdown>{msg.text}</ReactMarkdown></p>
                {/* NEW: Display attached files for user messages */}
                {msg.sender === 'user' && msg.files && msg.files.length > 0 && (
                  <div className="mt-2 text-xs text-primary-foreground/80">
                    <p className="font-semibold">Attached Files:</p>
                    <ul className="list-disc list-inside">
                      {msg.files.map((file, index) => (
                        <li key={index}>{file.name} ({file.type.split('/')[1]})</li>
                      ))}
                    </ul>
                  </div>
                )}
                <span className="text-xs opacity-70 mt-1 block">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={conversationEndRef} />
      </div>
      <div className="flex flex-col mt-4 space-y-2 user-message-box">
        {/* NEW: Display selected files */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/20 text-sm">
            {attachedFiles.map((file) => (
              <span key={file.name} className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                {file.name}
                <button
                  type="button"
                  onClick={() => handleRemoveFile(file.name)}
                  className="ml-1 text-blue-600 hover:text-blue-900"
                  disabled={isSending}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center space-x-2">
          <Input
            type="text"
            placeholder="Type your message here..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            className="flex-grow"
          />
          {/* NEW: Hidden file input */}
          <input
            type="file"
            multiple
            accept="audio/*,image/*,text/plain"
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: 'none' }}
            disabled={isSending}
          />
          {/* NEW: Attach File Link */}
          <Button
            variant="link"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending}
            className="whitespace-nowrap"
          >
            Attach File
          </Button>
          <Button onClick={handleSendMessage} disabled={isSending || (inputMessage.trim() === '' && attachedFiles.length === 0)}>
            {isSending ? <Spinner size="sm" /> : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantPage;